import type { RestrictionType } from '../whitelist-modal/sso-whitelist-type-modal';

import {
  GhostButton,
  IconButton,
  Modal,
  Paragraph,
  PrimaryButton,
  Tooltip,
  TrashIcon,
  UserIcon,
} from '@movingimage-evp/mi-ui-component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Search } from '../../../../components/search';
import { ListItemsPagination } from '../list-items-pagination';

import styles from './whitelist.module.css';

type WhitelistProps = {
  type: RestrictionType;
  items: string[];
  itemsPerPage?: number;
  onRemove: (item: string) => void;
  onRemoveAll: () => void;
};

export function Whitelist({ type, items, itemsPerPage = 1000, onRemove, onRemoveAll }: WhitelistProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [deleteConfirmationVisible, setDeleteConfirmationVisible] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);

  const testId = `${type}-whitelist`;

  if (items.length === 0) return null;

  const showDeleteConfirmationModal = () => {
    setDeleteConfirmationVisible(true);
  };

  const closeDeleteConfirmationModal = () => {
    setDeleteConfirmationVisible(false);
  };

  const handleRemoveAll = () => {
    closeDeleteConfirmationModal();
    onRemoveAll();
  };

  const filteredItems = items
    .filter((item) => item.toLowerCase().includes(search.toLowerCase()))
    .slice(0, itemsPerPage * pageNumber);

  return (
    <div data-testid={testId} className={styles.wrapper}>
      <header>
        <span data-testid={`${testId}-length`}>
          {t(`manager.webcastSetup.eventAccess.whitelist.attendesNumber.${type}`)} ({items.length})
        </span>

        <GhostButton data-testid={`${testId}-delete-all-button`} small onClick={showDeleteConfirmationModal}>
          {t('manager.webcastSetup.eventAccess.whitelist.deleteAll')}
        </GhostButton>
      </header>

      <Search
        placeholder={t('manager.webcastSetup.eventAccess.whitelist.searchForValue')}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        onClear={() => setSearch('')}
      />

      <div data-testid={`${testId}-items`} className={styles.items}>
        {filteredItems.map((item, index) => (
          <div key={index} data-testid={`${testId}-value-${index}`} className={styles.item}>
            <span>
              <UserIcon />
              {item}
            </span>

            <Tooltip label={t('manager.webcastSetup.eventAccess.whitelist.removeValue')}>
              <IconButton
                data-testid={`${testId}-value-${index}-remove-button`}
                type="button"
                aria-label={t('manager.webcastSetup.eventAccess.whitelist.removeValue')}
                rounded
                onClick={() => onRemove(item)}
              >
                <TrashIcon />
              </IconButton>
            </Tooltip>
          </div>
        ))}

        <ListItemsPagination
          current={itemsPerPage}
          total={items.length}
          onShowMore={() => setPageNumber(pageNumber + 1)}
        />

        {filteredItems.length === 0 && (
          <Paragraph data-testid={`${testId}-no-results`}>
            {t('manager.webcastSetup.eventAccess.whitelist.noValuesFound')}
          </Paragraph>
        )}
      </div>

      <Modal
        data-testid="delete-confirmation-modal"
        title={t('manager.webcastSetup.eventAccess.whitelist.deleteConfirmationModal.title')}
        isOpen={deleteConfirmationVisible}
        onClose={closeDeleteConfirmationModal}
        footer={
          <div className={styles.deleteConfirmationModalFooter}>
            <GhostButton
              data-testid="delete-confirmation-modal-cancel-button"
              small
              onClick={closeDeleteConfirmationModal}
            >
              {t('manager.webcastSetup.eventAccess.whitelist.deleteConfirmationModal.footer.cancel')}
            </GhostButton>

            <PrimaryButton data-testid="delete-confirmation-modal-submit-button" small onClick={handleRemoveAll}>
              {t('manager.webcastSetup.eventAccess.whitelist.deleteConfirmationModal.footer.confirm')}
            </PrimaryButton>
          </div>
        }
      >
        <Paragraph>{t('manager.webcastSetup.eventAccess.whitelist.deleteConfirmationModal.description')}</Paragraph>
      </Modal>
    </div>
  );
}
